/** @jsxImportSource preact */
import { useEffect, useState } from 'preact/hooks'
import { callApi } from '../api'

/**
 * Bringing the old Allowed DF spreadsheet across, for admins.
 *
 * Kept closed, and out of sight for everyone else: it is a one-off that writes many approvals at
 * once. It always shows what a file would do before it lets anyone write it, and asks again if the
 * file or the balance option changes after that.
 */

type Balance = { kind: 'owed'; amount: number } | { kind: 'settled' } | { kind: 'unverified' } | { kind: 'unreadable' }

interface RowResult {
  bookingReference: string
  status: 'have' | 'booking-unreadable' | 'ready' | 'written' | 'race'
  notDefaulted: boolean
  balance: Balance | null
}

interface Report {
  committed: boolean
  skipped: { line: number; reason: string }[]
  results: RowResult[]
  importable: number
}

const IMPORT_PATH = '/bookings/fraud/default-waiver/import'

function Group({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null

  return (
    <div className="mt-3">
      <h4 className="text-sm font-semibold text-gray-800">
        {title} ({items.length})
      </h4>
      <ul className="mt-1 text-sm text-gray-600 font-mono">
        {items.map(item => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

export function FraudWaiverImport({ onImported }: { onImported: () => void }) {
  const [isAdmin, setIsAdmin] = useState(false)
  const [fileName, setFileName] = useState('')
  const [csv, setCsv] = useState('')
  const [strict, setStrict] = useState(false)
  // The report remembers exactly what it was run on. Import is offered only for that file and that
  // option, so a check that lands late, or a changed option, can never commit something unseen.
  const [checked, setChecked] = useState<{ report: Report; csv: string; strict: boolean } | null>(null)
  const report = checked && checked.csv === csv && checked.strict === strict ? checked.report : null
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Anyone who cannot use it never sees it. A failed lookup leaves it hidden, which is the safe way to fail.
  useEffect(() => {
    callApi<{ youAreAdmin: boolean }>('/bookings/fraud/waiver-operators', {}, 'Could not load the approved agents')
      .then(data => setIsAdmin(data.youAreAdmin))
      .catch(() => setIsAdmin(false))
  }, [])

  if (!isAdmin) return null

  const send = async (commit: boolean) => {
    const sent = { csv, strict }
    setBusy(true)
    setError('')
    try {
      const next = await callApi<Report>(
        IMPORT_PATH,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ csv: sent.csv, commit, requireZeroBalance: sent.strict }),
        },
        commit ? 'Could not import the waivers' : 'Could not check the file',
      )
      setChecked({ report: next, csv: sent.csv, strict: sent.strict })
      if (commit) onImported()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the file')
    } finally {
      setBusy(false)
    }
  }

  const choose = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0]
    setError('')
    if (!file) {
      setFileName('')
      setCsv('')
      return
    }
    // Read first, then set name and content together, so a failed read cannot leave one file's
    // name beside another file's rows.
    try {
      const text = await file.text()
      setFileName(file.name)
      setCsv(text)
    } catch {
      setFileName('')
      setCsv('')
      setError('Could not read that file. Try saving it as CSV again.')
    }
  }

  const refs = (pick: (result: RowResult) => boolean) =>
    (report?.results ?? []).filter(pick).map(result => result.bookingReference)
  const owed = (report?.results ?? []).filter(result => result.balance?.kind === 'owed')

  return (
    <details className="mt-8 border border-gray-200 rounded-md">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700">
        Import waivers from a spreadsheet (admins)
      </summary>

      <div className="px-4 pb-4">
        <p className="text-sm text-gray-600 mb-3">
          For the old Allowed DF sheet. Save it as CSV. It needs the columns <em>DF Booking</em> and <em>Approval</em>;
          only ticked rows are used. Every row is checked first and nothing is written until you import. Up to 75 rows
          at a time; use the script for more.
        </p>

        {error && (
          <div
            role="alert"
            className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-red-700"
          >
            {error}
          </div>
        )}

        <label
          htmlFor="waiver-import-file"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          CSV file
        </label>
        <input
          id="waiver-import-file"
          type="file"
          accept=".csv,text/csv"
          onChange={choose}
          disabled={busy}
          className="text-sm"
        />

        <label
          htmlFor="waiver-import-strict"
          className="flex items-start gap-2 mt-3 text-sm text-gray-700"
        >
          <input
            id="waiver-import-strict"
            type="checkbox"
            checked={strict}
            disabled={busy}
            onChange={e => {
              setStrict((e.target as HTMLInputElement).checked)
            }}
            className="mt-1"
          />
          <span>
            Hold each waiver until its balance is cleared. Off by default: these were approved under the old process,
            and holding them now would undo decisions the team already made.
          </span>
        </label>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={busy || !csv}
            onClick={() => send(false)}
            className="px-4 py-2 bg-gray-700 text-white rounded-md disabled:bg-gray-300"
          >
            {busy && !report ? 'Checking…' : 'Check the file'}
          </button>
          {report && !report.committed && report.importable > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => send(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md disabled:bg-gray-300"
            >
              {busy ? 'Importing…' : `Import ${report.importable} waiver${report.importable === 1 ? '' : 's'}`}
            </button>
          )}
        </div>

        {report && (
          <div
            className="mt-4 text-sm"
            aria-live="polite"
          >
            <p className={report.committed ? 'text-green-800 font-medium' : 'text-gray-800 font-medium'}>
              {report.committed
                ? `Imported ${refs(r => r.status === 'written').length} waiver(s) from ${fileName}.`
                : `${fileName}: ${report.importable} would be imported. Nothing has been written.`}
            </p>
            <Group
              title="Already waived, left alone"
              items={refs(r => r.status === 'have')}
            />
            <Group
              title="Booking could not be read, not imported"
              items={refs(r => r.status === 'booking-unreadable')}
            />
            <Group
              title="Still owe money. Review these"
              items={owed.map(r => `${r.bookingReference} (${r.balance?.kind === 'owed' ? r.balance.amount : ''})`)}
            />
            <Group
              title="Balance not verified (Stripe plan or source unknown). Check in Stripe"
              items={refs(r => r.balance?.kind === 'unverified')}
            />
            <Group
              title="Balance could not be read. Check by hand"
              items={refs(r => r.balance?.kind === 'unreadable')}
            />
            <Group
              title="Not currently marked as unpaid (imported, but does nothing)"
              items={refs(r => r.notDefaulted)}
            />
            <Group
              title="Rows skipped"
              items={report.skipped.map(row => `line ${row.line}: ${row.reason}`)}
            />
          </div>
        )}
      </div>
    </details>
  )
}
